import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "lh-cookie-consent";

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      if (!v) setVisible(true);
    } catch (err) {
      // localStorage blocked (private browsing, Safari ITP, etc.) — show the banner
      // so we still meet the consent-disclosure obligation.
      console.warn("CookieBanner: localStorage unavailable, showing banner.", err?.message);
      setVisible(true);
    }
  }, []);

  const persist = (value) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ value, at: Date.now() }));
    } catch (err) {
      // Can't persist — the choice still applies for this session.
      console.warn("CookieBanner: could not persist consent choice.", err?.message);
    }
    // Update Google Analytics Consent Mode in real time.
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("consent", "update", {
        analytics_storage: value === "all" ? "granted" : "denied",
        ad_storage: "denied",
        ad_user_data: "denied",
        ad_personalization: "denied",
      });
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      className="fixed bottom-2 left-2 right-2 lg:left-auto lg:right-6 lg:bottom-6 lg:max-w-md z-[60] rounded-2xl shadow-2xl"
      style={{
        background: "var(--lh-espresso-noir)",
        color: "var(--lh-warm-ivory)",
        border: "1px solid rgba(255, 168, 115, 0.25)",
      }}
      data-testid="cookie-banner"
    >
      {/* Mobile (compact, single-row) — only visible below lg */}
      <div className="lg:hidden flex items-center gap-2 px-3 py-2.5">
        <Cookie
          size={16}
          className="shrink-0"
          style={{ color: "var(--lh-peach)" }}
          aria-hidden="true"
        />
        <p
          className="flex-1 text-[0.78rem] leading-tight font-medium"
          style={{ color: "rgba(234, 216, 199, 0.92)" }}
        >
          We use essential cookies and anonymous analytics.{" "}
          <Link
            to="/privacy"
            className="underline underline-offset-2 whitespace-nowrap"
            style={{ color: "var(--lh-peach)" }}
          >
            Privacy
          </Link>
        </p>
        <button
          onClick={() => persist("all")}
          className="shrink-0 px-3 py-1.5 rounded-full text-[0.7rem] font-bold uppercase tracking-widest"
          style={{
            background: "var(--lh-peach)",
            color: "var(--lh-espresso-noir)",
          }}
          data-testid="cookie-accept"
        >
          OK
        </button>
        <button
          onClick={() => persist("essential")}
          aria-label="Close cookie notice"
          className="shrink-0 p-1 rounded-full hover:bg-white/10 transition-colors"
          style={{ color: "var(--lh-warm-ivory)" }}
          data-testid="cookie-close"
        >
          <X size={14} />
        </button>
      </div>

      {/* Desktop (full version) */}
      <div className="hidden lg:block p-6">
        <div className="flex items-start gap-3">
          <Cookie
            size={20}
            className="shrink-0 mt-0.5"
            style={{ color: "var(--lh-peach)" }}
            aria-hidden="true"
          />
          <div className="flex-1">
            <div
              className="overline mb-2"
              style={{ color: "var(--lh-peach)" }}
            >
              Cookies
            </div>
            <p
              className="text-sm leading-relaxed font-medium"
              style={{ color: "rgba(234, 216, 199, 0.92)" }}
            >
              We use a few essential cookies to keep you signed in, and anonymous analytics to
              improve the platform. Read our{" "}
              <Link
                to="/privacy"
                className="underline underline-offset-2"
                style={{ color: "var(--lh-peach)" }}
              >
                Privacy Policy
              </Link>{" "}
              for the full list.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                onClick={() => persist("all")}
                className="px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider transition-colors"
                style={{
                  background: "var(--lh-peach)",
                  color: "var(--lh-espresso-noir)",
                }}
                data-testid="cookie-accept-desktop"
              >
                Accept all
              </button>
              <button
                onClick={() => persist("essential")}
                className="px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider transition-colors"
                style={{
                  background: "transparent",
                  color: "var(--lh-warm-ivory)",
                  border: "1px solid rgba(234, 216, 199, 0.35)",
                }}
                data-testid="cookie-essential-only"
              >
                Essential only
              </button>
            </div>
          </div>
          <button
            onClick={() => persist("essential")}
            aria-label="Close cookie notice"
            className="shrink-0 p-1 rounded-full hover:bg-white/10 transition-colors"
            style={{ color: "var(--lh-warm-ivory)" }}
            data-testid="cookie-close-desktop"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
